import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden">
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
            <div className="lg:w-[55%] pt-16 md:pt-20">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-background-50 ${slide.tagColor} mb-4`}>
                {slide.tag}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading text-background-50 leading-tight mb-3">
                {slide.title}
              </h1>
              <p className="text-xl md:text-2xl text-background-100/90 font-medium mb-4">
                {slide.subtitle}
              </p>
              <p className="text-sm md:text-base text-background-200 max-w-lg leading-relaxed">
                {slide.description}
              </p>
              <div className="flex items-center gap-3 mt-6">
                <Link
                  to={slide.link}
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  立即观看
                </Link>
                <Link
                  to="/category/guoman"
                  className="px-6 py-3 rounded-lg border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  更多推荐
                </Link>
              </div>
            </div>
            <div className="lg:w-[40%] hidden lg:block"></div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-primary-500 w-7' : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 flex items-center justify-center transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}